import { Box } from "@radix-ui/themes";
import { on } from "events";
import { useEffect, useState } from "react";
import { RxDotFilled } from "react-icons/rx";

interface MinMaxScaleProps {
  minLabel: string;
  maxLabel: string;
  minValue: number;
  maxValue: number;
  step: number;
  value: number;
  inLine: boolean;
  onScale: (value: number) => void;
}

const InlineMinMaxScale = ({ minLabel, maxLabel, minValue, maxValue, step, value, onScale }: MinMaxScaleProps) => {
  const generateDots = (min: number, max: number, step: number) => {
    const dots = [];
    for (let i = min; i <= max; i += step) {
      dots.push(i);
    }
    return dots;
  };

  const dots = generateDots(minValue, maxValue, step);

  return (
    <div className="flex w-full border shadow-lg rounded-t-2xl">
      <div className="flex flex-col bg-[#D7D3E3] rounded-xl shadow-lg">
        <div className="flex text-xs justify-between">
          <label className="flex flex-col font-medium">
            <span className="text-purple-700">{minLabel}</span>
          </label>
          <label className="flex flex-col font-medium">
            <span className="text-purple-700">{maxLabel}</span>
          </label>
        </div>
        <div className="flex text-xs justify-between px-1 my-3">
          <label className="flex flex-col font-medium">
            <span className="font-medium">{minValue}</span>
          </label>
          <label className="flex flex-col font-medium gap-3">
            <span className="font-medium">{Math.floor((minValue + maxValue) / 2)}</span>
          </label>
          <label className="flex flex-col font-medium">
            <span className="text-purple-700">{maxValue}</span>
          </label>
        </div>

        <div className="flex justify-between relative w-full border border-purple-800 bg-white rounded-3xl">
          {dots.map((item, index) => (
            <div className="flex" key={`${item}-${index}`}>
              <span>
                <RxDotFilled />
              </span>
            </div>
          ))}
          <input
            type="range"
            className="absolute top-[50%] w-full accent-purple-700 appearance-none h-0 [&::-webkit-range-thumb]:h-[50px] [&::-webkit-slider-thumb]:w-[50px] [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-purple-500"
            min={minValue}
            max={maxValue}
            value={`${value}`}
            step={step}
            onChange={(e) => onScale(parseInt(e.target.value))}
          />
        </div>
      </div>
      <div>
        <label className="flex px-12 my-[5%] border rounded-2xl text-white py-2 bg-gradient-to-br from-purple-950 to-purple-500">
          {value}
        </label>
      </div>
    </div>
  );
};

const MinMaxScale = ({ minLabel, maxLabel, minValue, maxValue, step, value, onScale }: MinMaxScaleProps) => {
  const generateDots = (min: number, max: number, step: number) => {
    const dots = [];
    for (let i = min; i <= max; i += step) {
      dots.push(i);
    }
    return dots;
  };

  const dots = generateDots(minValue, maxValue, step);

  return (
    <div className="flex flex-col border shadow-lg max-w-3xl rounded-t-2xl">
      <div className="flex flex-col max-w-3xl px-8 mobile:px-4 tablet:px-4 py-10 bg-[#D7D3E3] rounded-xl shadow-lg">
        <div className="flex text-xs justify-between py-2">
          <label className="flex flex-col font-medium">
            <span className="text-purple-700">{minLabel}</span>
          </label>
          <label className="flex flex-col font-medium">
            <span className="text-purple-700">{maxLabel}</span>
          </label>
        </div>
        <div className="flex text-xs justify-between px-1 my-3">
          <label className="flex flex-col font-medium">
            <span className="font-medium">{minValue}</span>
          </label>
          <label className="flex flex-col font-medium gap-3">
            <span className="font-medium">{Math.floor((minValue + maxValue) / 2)}</span>
          </label>
          <label className="flex flex-col font-medium">
            <span className="text-purple-700">{maxValue}</span>
          </label>
        </div>

        <div className="flex justify-between relative w-full border border-purple-800 bg-white rounded-3xl">
          {dots.map((item, index) => (
            <div className="flex" key={`${item}-${index}`}>
              <span>
                <RxDotFilled />
              </span>
            </div>
          ))}
          <input
            type="range"
            className="absolute top-[50%] w-full accent-purple-700 appearance-none h-0 [&::-webkit-range-thumb]:h-[50px] [&::-webkit-slider-thumb]:w-[50px] [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-purple-500"
            min={minValue}
            max={maxValue}
            value={`${value}`}
            step={step}
            onChange={(e) => onScale(parseInt(e.target.value))}
          />
        </div>
      </div>
      <div className="flex gap-2 justify-center">
        <label className="text-md my-auto font-medium text-purple-700">
          Your Response
        </label>
        <label className="flex px-12 my-[5%] border rounded-2xl text-white py-2 bg-gradient-to-br from-purple-950 to-purple-500">
          {value}
        </label>
      </div>
    </div>
  );
};

interface MinMaxRatingProps {
  element: any;
  index: number;
  wantOptionRating: boolean;
  onScale: (option: any, scale: string, index: number) => void;
  onValidate?: (index: number, isValid: boolean) => void;
  isMandatory?: boolean;
}

export const MinMaxRating = ({ element, index, onScale, wantOptionRating, onValidate, isMandatory }: MinMaxRatingProps) => {
  const [isOptionRating, setOptionRating] = useState(wantOptionRating);

  return (
    <Box>
      {!isOptionRating && <MinMaxQuestion isMandatory={isMandatory} index={index}
        onValidate={onValidate} onScale={(option, scale) => onScale(option, scale, index)} element={element} />}
      {isOptionRating && <MinMaxOption isMandatory={isMandatory} index={index}
        onValidate={onValidate} onScale={(option, scale) => onScale(option, scale, index)} element={element} />}
    </Box>
  );
};

export const MinMaxQuestion = ({ element, onScale, isMandatory, onValidate, index }: { element: any; index: any; isMandatory?: boolean; onValidate?: (index: number, isValid: boolean) => void; onScale: (option: any, scale: string) => void }) => {
  const minLabel = element.minValuelabel || element.minLabel || "Min";
  const maxLabel = element.maxValuelabel || element.maxLabel || "Max";
  const minValue = parseInt(element.minValue) || -5;
  const maxValue = parseInt(element.maxValue) || 5;
  const step = parseInt(element.incrementalUnit) || 1;

  const value = element.answer && element.answer[0] && element.answer[0].value !== undefined
    ? parseInt(element.answer[0].value) || 0
    : 0;

  const option = (element && element.answer && element.answer[0])
    ? element.answer[0]
    : (element.answerOptions && element.answerOptions[0])
      ? { value: element.answerOptions[0].answerOption, isAnswer: element.answerOptions[0].rightAnswer }
      : {};

  useEffect(() => {
    if (isMandatory) {
      onValidate(index, value !== 0); // must choose something
    } else {
      onValidate(index, true);
    }
  }, [value, index, isMandatory, onValidate]);

  return (
    <Box>
      <label className="block mb-2 text-normal font-bold text-app-gray-medium">
        {element.question}
        {isMandatory && <span className="text-red-600">*</span>}
      </label>
      <MinMaxScale
        value={value}
        minLabel={minLabel}
        maxLabel={maxLabel}
        minValue={minValue}
        maxValue={maxValue}
        inLine={false}
        onScale={(scale: number) => {
          onScale(option, scale.toString());
          if (onValidate && isMandatory) {
            onValidate(index, true);
          }
        }}
        step={step}
      />
    </Box>
  );
};

export const MinMaxOption = ({ element, onScale, isMandatory, onValidate, index }: { element: any; index: any; isMandatory?: boolean; onValidate?: (index: number, isValid: boolean) => void; onScale: (option: any, scale: string) => void }) => {
  const minLabel = element.minValuelabel || element.minLabel || "Min";
  const maxLabel = element.maxValuelabel || element.maxLabel || "Max";
  const minValue = parseInt(element.minValue) || -5;
  const maxValue = parseInt(element.maxValue) || 5;
  const step = parseInt(element.incrementalUnit) || 1;

  const options = (element && element.answer)
    ? element.answer.map((el: any) => ({ value: el.value, isAnswer: el.isAnswer, answerId: el.answerId }))
    : (element && element.answerOptions)
      ? element.answerOptions.map((el: any) => ({ value: el.answerOption, isAnswer: el.isAnswer || "", answerId: el.answerId || "" }))
      : [];

  const [scales, setScales] = useState(options);
  useEffect(() => {
    if (onValidate) {
      if (isMandatory) {
        const anySelected = scales.some((s) => s.isAnswer === true && parseInt(s.value) > 0);
        onValidate(index, anySelected);
      } else {
        onValidate(index, true);
      }
    }
  }, [scales, index, isMandatory, onValidate]);

  return (
    <Box className="w-full">
      <label className="block mb-2 text-sm mobile:text-xs font-bold text-app-gray-medium">
        {element.question}
        {isMandatory && <span className="text-red-600">*</span>}
      </label>

      {options.map((el: any, index: number) => (
        <Box
          key={index}
          className="flex flex-col mobile:flex-col tablet:flex-row justify-between w-full gap-2"
        >
          {/* Label */}

          <div className="w-full mobile:w-full tablet:w-1/2 shadow-md rounded-2xl p-2 text-center tablet:text-left text-sm mobile:text-xs">
            {el.value}
          </div>

          {/* Slider */}
          <div className="w-full tablet:w-1/2">
            <InlineMinMaxScale
              value={parseInt(el.value) || 0}
              minLabel={minLabel}
              maxLabel={maxLabel}
              minValue={minValue}
              maxValue={maxValue}
              inLine={true}
              onScale={(scale: number) => {
                const updatedScales = [...scales];
                updatedScales[index] = {
                  ...el,
                  value: scale.toString(),
                  isAnswer: true,
                };
                setScales(updatedScales);
                onScale(el, scale.toString());

                if (onValidate && isMandatory) {
                  onValidate(index, true);
                }
              }}
              step={step}
            />
          </div>
        </Box>
      ))}
    </Box>

  );
};

export default MinMaxRating;